/**
 * IndexedDB persistence for user status.
 * One record per userId. Last-write-wins on reload.
 */

import type { AtWorkLocation, UserStatus } from './types';

const DB_NAME = 'campus-status-db';
const DB_VERSION = 1;
const STORE_NAME = 'status';
const KEY_PATH = 'userId';

export interface IStoredStatusEmergencyContactBusy {
    inviteToRoom: boolean;
    textMessage: boolean;
}

export interface IStoredStatusEmergencyContactOoo {
    email: boolean;
    chat: boolean;
    sms: boolean;
    call: boolean;
}

export type IStoredEmergencyContact = IStoredStatusEmergencyContactBusy | IStoredStatusEmergencyContactOoo | null;

/** Full status snapshot - use useStatusSnapshot() to get from Recoil. */
export interface IStatusSnapshot {
    userId: string;
    status: UserStatus;
    location: AtWorkLocation | null;
    phrase: string | null;
    /** Duration in ms (busy/on-the-phone). */
    duration: number | null;
    /** End timestamp in ms (busy/on-the-phone). */
    endTime?: number | null;
    /** Duration in minutes for countdown display (busy/on-the-phone). */
    durationMinutes?: number | null;
    returnDate: string | null;
    returnTime: string | null;
    absenceComment: string | null;
    emergencyContact: IStoredEmergencyContact;
}

export interface IStoredStatus extends IStatusSnapshot {
    version: number;
}

/**
 * localStorage key a tab writes after it stored a changed status in IndexedDB. Other tabs of the same
 * browser get a `storage` event and re-read IndexedDB, so every tab holds the same own-status.
 */
export const STATUS_CHANGED_STORAGE_KEY = 'statusChangedAt';

function sortKeysDeep(value: unknown): unknown {
    if (Array.isArray(value)) return value.map(sortKeysDeep);
    if (value && typeof value === 'object') {
        return Object.keys(value as Record<string, unknown>)
            .sort()
            .reduce<Record<string, unknown>>((acc, key) => {
                acc[key] = sortKeysDeep((value as Record<string, unknown>)[key]);
                return acc;
            }, {});
    }
    return value;
}

/** Stable JSON of a stored status without `version` (keys sorted, so property order doesn't matter). */
function comparableStatusJson(value: IStoredStatus): string {
    const rest: Partial<IStoredStatus> = { ...value };
    delete rest.version;
    return JSON.stringify(sortKeysDeep(rest));
}

/** True when both records hold the same status data (ignores `version`). */
export function isSameStoredStatus(a: IStoredStatus | null, b: IStoredStatus | null): boolean {
    if (!a || !b) return a === b;
    return comparableStatusJson(a) === comparableStatusJson(b);
}

/** How long a socket-delivered status update wins over a concurrent API fetch. */
const SOCKET_FRESH_MS = 5000;

const lastSocketStatusAt: Record<string, number> = {};

export function markSocketStatusUpdate(userId: string): void {
    lastSocketStatusAt[userId] = Date.now();
}

export function isSocketStatusFresh(userId: string): boolean {
    const ts = lastSocketStatusAt[userId];
    return ts != null && Date.now() - ts < SOCKET_FRESH_MS;
}

function openDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onerror = () => reject(req.error);
        req.onsuccess = () => resolve(req.result);
        req.onupgradeneeded = (e) => {
            const db = (e.target as IDBOpenDBRequest).result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME, { keyPath: KEY_PATH });
            }
        };
    });
}

export async function getStoredStatus(userId: string): Promise<IStoredStatus | null> {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);
        const req = store.get(userId);
        req.onerror = () => reject(req.error);
        req.onsuccess = () => resolve(req.result ?? null);
        db.close();
    });
}

export async function putStoredStatus(data: IStoredStatus): Promise<void> {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        const payload: IStoredStatus = { ...data, version: 1 };
        const req = store.put(payload);
        req.onerror = () => reject(req.error);
        req.onsuccess = () => resolve();
        db.close();
    });
}

export async function getAllStoredStatuses(): Promise<IStoredStatus[]> {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);
        const req = store.getAll();
        req.onerror = () => reject(req.error);
        req.onsuccess = () => resolve(req.result ?? []);
        db.close();
    });
}
